import { Box, Text, Title } from "@mantine/core";
import { useState } from "react";
import { type ExtendedUser, type Todo } from "../../../utils/backend-types";
import { ClarifyTodoModal } from "../ClarifyTodoModal/ClarifyTodoModal";
import { TodoDecisionList } from "../TodoDecisionList/TodoDecisionList";
import {
  DECISION_TEXTS,
  TODO_SYSTEM_DECISION_BY_USER_DECISION
} from "../Todos.constants";
import { TodoSystemDecision, type TodoUserDecision } from "../Todos.types";

const SYSTEM_DECISION_ORDER = [
  TodoSystemDecision.Applicable,
  TodoSystemDecision.CantTell,
  TodoSystemDecision.NotApplicable
];

interface TodoDecisionWidgetProps {
  todos: Array<Todo>;
  userDecisions: Record<number, TodoUserDecision>;
  finishedTodoIds: Array<number>;
  onChangeUserDecisionAndFinished: (
    todoId: number,
    userDecision: TodoUserDecision,
    finished: boolean
  ) => void;
  users: Array<ExtendedUser>;
}

function TodoDecisionWidget({
  todos,
  userDecisions,
  finishedTodoIds,
  onChangeUserDecisionAndFinished,
  users
}: TodoDecisionWidgetProps) {
  const [todoToClarify, setTodoToClarify] = useState<Todo | null>(null);
  const [clarifiedTodoIds, setClarifiedTodoIds] = useState<Array<number>>([]);

  function groupTodosByUserDecisionOrSystemDecision(todos: Array<Todo>) {
    return todos.reduce((groupedTodos, todo) => {
      let systemDecisionKey;

      const systemDecisionForUserDecision =
        TODO_SYSTEM_DECISION_BY_USER_DECISION[todo.userDecision];
      const systemDecisionKeyForUserDecision = Object.values(
        TodoSystemDecision
      ).find(
        (todoSystemDecision) =>
          todoSystemDecision === systemDecisionForUserDecision
      );

      if (systemDecisionForUserDecision) {
        systemDecisionKey = systemDecisionKeyForUserDecision;
      } else if (
        todo.systemDecision === TodoSystemDecision.NeedMoreInformation
      ) {
        // this todo is open and can be clarified - we want to group it with the other "zu klären" todos
        systemDecisionKey = Object.values(TodoSystemDecision).find(
          (todoSystemDecision) =>
            todoSystemDecision === TodoSystemDecision.CantTell
        );
      } else {
        systemDecisionKey = Object.values(TodoSystemDecision).find(
          (todoSystemDecision) => todoSystemDecision === todo.systemDecision
        );
      }

      if (groupedTodos[systemDecisionKey]) {
        groupedTodos[systemDecisionKey].push(todo);
      } else {
        groupedTodos[systemDecisionKey] = [todo];
      }

      return groupedTodos;
    }, {});
  }

  function handleClickClarify(todo: Todo) {
    setTodoToClarify(todo);
  }

  function handleTodoClarified(
    clarified: boolean,
    userDecision: TodoUserDecision,
    finished: boolean
  ) {
    if (todoToClarify) {
      onChangeUserDecisionAndFinished(todoToClarify.id, userDecision, finished);

      if (clarified) {
        setClarifiedTodoIds([...clarifiedTodoIds, todoToClarify.id]);
      }
    }
  }

  const orderedTodos = groupTodosByUserDecisionOrSystemDecision(todos);

  const orderedTodoKeys = SYSTEM_DECISION_ORDER.filter((decision) =>
    Object.keys(orderedTodos).includes(decision)
  );

  return (
    <div className="TodoDecisionWidget">
      <Text mb="md">
        Folgende Aufgaben können für Ihr Projekt einschlägig sein. Sie können
        diese in Ihre Aufgabenliste übernehmen.
      </Text>
      {orderedTodoKeys.map((orderedTodoKey) => (
        <Box key={orderedTodoKey} mb="lg">
          <Title mb="xs" order={5}>
            {DECISION_TEXTS[orderedTodoKey].displayName}
          </Title>
          <Text mb="sm">{DECISION_TEXTS[orderedTodoKey].explanation}</Text>
          <TodoDecisionList
            clarifiedTodoIds={clarifiedTodoIds}
            finishedTodoIds={finishedTodoIds}
            todos={orderedTodos[orderedTodoKey]}
            userDecisions={userDecisions}
            users={users}
            onChangeUserDecisionAndFinished={onChangeUserDecisionAndFinished}
            onClickClarify={handleClickClarify}
          />
        </Box>
      ))}
      {!!todoToClarify && (
        <ClarifyTodoModal
          currentUserDecision={userDecisions[todoToClarify.id]}
          isOpen={!!todoToClarify}
          todo={todoToClarify}
          todoIsFinished={finishedTodoIds.includes(todoToClarify.id)}
          onTodoClarified={handleTodoClarified}
          onToggle={() => setTodoToClarify(null)}
        />
      )}
    </div>
  );
}

export { TodoDecisionWidget, TodoDecisionWidgetProps };
